import { useState, useRef, useEffect, useCallback, useId } from "react";
import { MessageCircleHeart, Send, X } from "lucide-react";
import { API } from "@/lib/api";

const SUGGESTED = [
  "I'd like to volunteer this weekend near me.",
  "How do I sponsor a housing project?",
  "Suggest ways to help elderly neighbors.",
  "I need help paying medical bills — where do I start?",
];

// Local id helper so each message has a stable key (never use array index).
let _mid = 0;
const nextMid = () => {
  _mid += 1;
  return `m-${Date.now()}-${_mid}`;
};

function parseSseChunk(raw) {
  // Splits an SSE buffer on double newlines and returns [events, remainder]
  const parts = raw.split("\n\n");
  const remainder = parts.pop() || "";
  const events = [];
  for (const p of parts) {
    if (!p.startsWith("data: ")) continue;
    try {
      events.push(JSON.parse(p.slice(6)));
    } catch (err) {
      // Malformed chunk — keep debuggable, do not crash the stream.
      if (process.env.NODE_ENV === "development") {
        console.warn("AIHelper: skipping malformed SSE chunk", err);
      }
    }
  }
  return { events, remainder };
}

export const AIHelper = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const inputId = useId();

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const appendAiDelta = useCallback((delta) => {
    setMessages((m) => {
      const copy = [...m];
      const last = copy[copy.length - 1];
      copy[copy.length - 1] = { ...last, text: last.text + delta };
      return copy;
    });
  }, []);

  const send = useCallback(
    async (text) => {
      const q = (text ?? input).trim();
      if (!q || busy) return;
      setInput("");
      setBusy(true);
      const userMsg = { id: nextMid(), role: "user", text: q };
      const aiPlaceholder = { id: nextMid(), role: "ai", text: "" };
      setMessages((m) => [...m, userMsg, aiPlaceholder]);

      try {
        const res = await fetch(`${API}/ai/chat`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: q, session_id: sessionRef.current }),
        });
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const { events, remainder } = parseSseChunk(buf);
          buf = remainder;
          for (const ev of events) {
            if (ev.delta) appendAiDelta(ev.delta);
            if (ev.session_id) sessionRef.current = ev.session_id;
            if (ev.error) throw new Error(ev.error);
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("AIHelper stream failed:", err);
        }
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = {
            ...copy[copy.length - 1],
            text: "Sorry — the AI helper is briefly unavailable. Try again in a moment.",
          };
          return copy;
        });
      } finally {
        setBusy(false);
      }
    },
    [input, busy, appendAiDelta],
  );

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      send();
    },
    [send],
  );

  return (
    <>
      <button
        data-testid="ai-helper-toggle"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 rounded-full p-4 shadow-lg transition-transform duration-200 hover:scale-105"
        style={{ background: "var(--wofy-primary)", color: "#FDFBF7" }}
        aria-label="Open AI helper"
      >
        <MessageCircleHeart className="w-6 h-6" />
      </button>

      {open && (
        <div
          data-testid="ai-helper-panel"
          className="fixed inset-x-4 bottom-4 md:inset-auto md:bottom-6 md:right-6 md:w-[420px] z-50 rounded-2xl border shadow-2xl flex flex-col overflow-hidden"
          style={{ background: "var(--wofy-bg)", borderColor: "var(--wofy-border)", maxHeight: "min(640px, 85vh)" }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--wofy-border)" }}>
            <div>
              <div className="overline">AI HELPER</div>
              <div className="font-display text-lg">How can we help today?</div>
            </div>
            <button
              data-testid="ai-close-btn"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-full hover:bg-[color:var(--wofy-surface)]"
              aria-label="Close AI helper"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
                  Tell us what kind of help you want to give or receive. We match volunteers, organizations, and projects worldwide.
                </p>
                <div className="grid gap-2">
                  {SUGGESTED.map((s) => (
                    <button
                      key={s}
                      data-testid={`ai-suggested-${s.slice(0, 10)}`}
                      onClick={() => send(s)}
                      className="text-left text-sm px-3 py-2 rounded-lg border transition-colors"
                      style={{ borderColor: "var(--wofy-border)" }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "text-right" : ""}`}>
                <div
                  className="inline-block max-w-[92%] px-3 py-2 rounded-2xl"
                  style={{
                    background: m.role === "user" ? "var(--wofy-primary)" : "var(--wofy-surface)",
                    color: m.role === "user" ? "#FDFBF7" : "var(--wofy-text)",
                  }}
                >
                  {m.text || (m.role === "ai" && busy ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={onSubmit} className="border-t p-3 flex items-center gap-2" style={{ borderColor: "var(--wofy-border)" }}>
            <input
              id={inputId}
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={busy}
              placeholder="Type your question…"
              className="flex-1 bg-transparent outline-none px-3 py-2 text-sm rounded-full border"
              style={{ borderColor: "var(--wofy-border)" }}
            />
            <button
              data-testid="ai-send-btn"
              type="submit"
              disabled={busy || !input.trim()}
              className="wofy-btn-primary !py-2 !px-4 disabled:opacity-50"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
